import type { GeneratedConfig } from '@ms-cloudpack/common-types';
import { writeJson } from '@ms-cloudpack/json-utilities';
import fs from 'fs';
import { getConfigPath } from '../getConfigPath.js';
import { generatedAppConfigSchemaUrl } from '../constants.js';
import { sortGeneratedConfig } from './sortGeneratedConfig.js';

/**
 * Comment added to the top of the generated file (exported for testing only).
 * @internal
 */
export const fileHeaderComment = `/**\n * THIS FILE IS AUTO-GENERATED.\n * DO NOT MANUALLY EDIT THIS FILE.\n */\n`;

/**
 * Clean up the generated config (sort it and remove empty settings) and write it to
 * cloudpack.generated.json. This is used at the end of `init`.
 *
 * If the config file exists but is no longer needed, it will be deleted.
 *
 * @returns
 * - `'written'` if the config was written
 * - `'deleted'` if the config previously existed but is now empty and was deleted
 * - `'no-op'` if the config didn't previously exist and is still empty
 */
export async function writeGeneratedConfig(
  config: GeneratedConfig,
  appPath: string,
): Promise<'written' | 'deleted' | 'no-op'> {
  const { generatedConfigPath } = getConfigPath(appPath);

  sortGeneratedConfig(config);

  cleanGeneratedConfig(config);

  if (!config.packageSettings?.length) {
    if (fs.existsSync(generatedConfigPath)) {
      fs.unlinkSync(generatedConfigPath);
      return 'deleted';
    }
    return 'no-op';
  }

  await writeJson(
    generatedConfigPath,
    {
      $schema: generatedAppConfigSchemaUrl,
      ...config,
    },
    { fileHeaderComment },
  );
  return 'written';
}

/**
 * Due to optimization, some package settings may end up with empty settings.
 * These would add useless bloat, so filter them out before writing the generated config.
 * This function removes those settings by mutating the input generated config.
 */
export function cleanGeneratedConfig(config: GeneratedConfig): void {
  if (!config.packageSettings) {
    return;
  }

  // Remove empty settings
  config.packageSettings.forEach((s) => {
    if (s.exports && Object.keys(s.exports).length === 0) {
      delete s.exports;
    }
    if (s.dynamicImports?.length === 0) {
      delete s.dynamicImports;
    }
    if (s.excludedDependencies?.length === 0) {
      delete s.excludedDependencies;
    }
    if (s.includedDependencies?.length === 0) {
      delete s.includedDependencies;
    }
  });

  config.packageSettings = config.packageSettings.filter((s) => Object.keys(s).length > 1);
}
